import React, { useState, useEffect } from "react";
import images from "../../assets/images/index";
import { Link } from "react-router-dom";
import CustomSelect from "../../components/customSelect/customSelect";
import { useAuth } from "../../auth/AuthProvider";
import { usePermissionSeedStore } from "../../store/usePermissionSeedStore";
import { RolePermissions, rolesObjType } from "../../types/api";
import { hasModuleAccess } from "../../hooks/permissionUtils";
import settingsBtIcon from "../../assets/images/settings-bt-icon.svg";

const options = [
  { value: "hyderabad", label: "Hyderabad" },
  { value: "bangalore", label: "Bangalore" },
  { value: "kolkata", label: "Kolkata" },
  { value: "delhi", label: "Delhi" },
  { value: "mumbai", label: "Mumbai" },
];

const Dashboard: React.FC = () => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState<boolean>(false);
  const [isOrgUser, setIsOrgUser] = useState<boolean>(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const { user } = useAuth();

  const seedData = usePermissionSeedStore((state) => state.seed);

  useEffect(() => {
    if (user) {
      setIsOrgAdmin(user.roles.includes("orgadmin"));
      setIsSuperAdmin(user.roles.includes("superadmin"));
      setIsOrgUser(user.roles.includes("orguser"));
    }
  }, [user]);
  const displayOrgAdminName =
    (user as any)?.org_admin_name || user?.username || "Admin";
  const displayOrgName = (user as any)?.org_name || "";

  const imageMap: Record<string, string> = {
    security: images.securityIcon,
    "utility systems including construction & renovation":
      images.constructionIcon,
    "fire safety": images.fireSystemsIcon,
    "laws & regulations": images.lawIcon,
    "washroom and area maintenance": images.maintenanceIcon,
    "external emergency preparedness": images.emergencyPreparednessIcon,
    "hazardous material and safety": images.hazardousIcon,
    "equipment management": images.equipmentMaintenanceIcon,
    "safety & facility rounds": images.safetyFacilityIcon,
    "incident management": images.alert,
  };

  const getImageForModule = (moduleName: string) => {
    return imageMap[moduleName.toLowerCase()] || images.securityIcon;
  };

  const accessibleModules = seedData?.filter((module) =>
    hasModuleAccess(seedData, module.key, user),
  );

  return (
    <div className="w-full px-4 mt-20 flex flex-col">
      {/* Welcome Banner Card */}
      <div className="bg-gradient-to-r from-teal-800 to-[#197f7d] text-white rounded-2xl p-6 md:p-8 shadow-md relative overflow-hidden mb-8">
        {/* Subtle background graphics */}
        <div className="absolute right-0 top-0 bottom-0 opacity-10 flex items-center pointer-events-none">
          <svg
            className="w-80 h-80 text-white transform translate-x-20 translate-y-10"
            fill="currentColor"
            viewBox="0 0 100 100"
          >
            <circle cx="50" cy="50" r="40" />
          </svg>
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <span className="bg-teal-700/60 text-teal-200 text-[10px] font-extrabold uppercase tracking-widest px-3 py-1 rounded-full border border-teal-600/30">
              {isOrgAdmin
                ? "Organization Administrator"
                : isSuperAdmin
                  ? "Super Administrator"
                  : "Organization User"}
            </span>
            <h1 className="text-3xl md:text-4xl font-black text-white mt-3 tracking-tight">
              Hello, {displayOrgAdminName}
            </h1>
            {displayOrgName && (
              <p className="text-sm md:text-base text-teal-100 mt-2 font-medium flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-teal-300 animate-pulse"></span>
                {displayOrgName}
              </p>
            )}
          </div>

          <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-teal-400 font-bold uppercase tracking-wider">
                Date & Time
              </p>
              <p className="text-sm font-semibold text-teal-700 mt-0.5">
                {new Date().toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="dashMainCards">
        {!(isSuperAdmin || isOrgAdmin) && accessibleModules.length === 0 && (
          <div className="flex flex-row justify-center">
            <div className="flex flex-col align-items-center py-5 my-5 mx-5 min-h-screen">
              <h3 className="font-semibold text-gray-500 pt-4 pr-4 ml-5">
                You have No Access Provided.
              </h3>
            </div>
          </div>
        )}
        <div className="row">
          {isSuperAdmin && (
            <>
              <div className="col-sm-3">
                <Link to="/tenant-management" className="card">
                  <img src={settingsBtIcon} alt="" />
                  <h3>Manage Organizations</h3>
                </Link>
              </div>
            </>
          )}
          {/* ///////// */}

          {/* ///////////////// */}
          {accessibleModules.map((module) => (
            <div key={module.key} className="col-sm-3">
              <Link to={module.key} className="card">
                <img src={getImageForModule(module.name)} alt={module.name} />
                <h3>{module.name}</h3>
              </Link>
            </div>
          ))}
          {/* <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.lawIcon} alt="" />
              <h3>LAWS & REGULATIONS</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="/fire-system" className="card">
              <img src={images.fireSystemsIcon} alt="" />
              <h3>FIRE SYSTEMS</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.constructionIcon} alt="" />
              <h3>UTILITY SYSTEMS INCLUDING CONSTRUCTION & RENOVATION</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.maintenanceIcon} alt="" />
              <h3>WASHROOM AND AREA MAINTENANCE</h3>
              <p>(including wheelchairs and stretchers)</p>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.emergencyPreparednessIcon} alt="" />
              <h3>EXTERNAL EMERGENCY PREPAREDNESS</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.hazardousIcon} alt="" />
              <h3>HAZARDOUS MATERIAL AND SAFETY</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.equipmentMaintenanceIcon} alt="" />
              <h3>EQUIPMENT MANAGEMENT</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.safetyFacilityIcon} alt="" />
              <h3>SAFETY & Facility rounds</h3>
            </Link>
          </div>

          <div className="col-sm-3">
            <Link to="" className="card">
              <img src={images.securityIcon} alt="" />
              <h3>SECURITY</h3>
            </Link>
          </div>
          <div className="col-sm-3">
            <Link to="/fire-safety-incident-admin" className="card">
              <img src={images.alert} alt="" />
              <h3>INCIDENT MANAGEMENT</h3>
            </Link>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
